<template>
  <div>
  <v-dialog v-model="dialog" width="1500" scrollable>
    <template v-slot:activator="{ on }">
      <v-btn
        tile
        v-if="isService"
        outlined
        height="40px"
        depressed
        v-on="on"
        color="primary"
      >
        Add Service Field
      </v-btn>
      <div v-on="on" v-else>
        <add-button v-if="!id"></add-button>
        <edit-icon-button v-else></edit-icon-button>
      </div>
    </template>
    <v-card class="service-field-form">
      <v-card-title>
        {{ title }}
      </v-card-title>
      <v-card-text class="pt-2">
        <v-form ref="form" v-model="isValid">
          <v-text-field
            label="Field Name"
            v-model="form.name"
            outlined
            :rules="rules"
            dense
          ></v-text-field>
          <v-autocomplete
            class="field-type-autocomplete"
            label="Field Type"
            item-text="name"
            :rules="rules"
            item-value="value"
            v-model="form.field_type"
            :items="types"
            outlined
            dense
          ></v-autocomplete>
          <v-row>
            <v-col cols="4" class="pt-0 pb-0">
              <v-checkbox
                label="Is Required?"
                hide-details=""
                v-model="form.is_required"
              ></v-checkbox>
            </v-col>
            <v-col cols="4" class="pt-0  pb-0">
              <v-checkbox
                label="Used in Calculation?"
                hide-details=""
                v-model="form.is_used"
              ></v-checkbox>
            </v-col>
            <v-col cols="4" class="pt-0  pb-0">
              <v-checkbox
                label="Is Disabled?"
                hide-details=""
                v-model="form.is_disabled"
              ></v-checkbox>
            </v-col>
            <v-col class="pt-0">
              <v-checkbox
                hide-details=""
                label="Searchable?"
                v-model="form.is_searchable"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-card
            outlined
            height="200px"
            class="service-list mt-5"
            v-if="!isService"
          >
            <v-card-text>
              <v-list>
                <v-list-item-group v-model="form.services" multiple>
                  <template v-for="(item, i) in templates">
                    <v-divider v-if="!item" :key="`divider-${i}`"></v-divider>
                    <v-list-item
                      v-else
                      :key="`item-${i}`"
                      :value="item.id"
                      active-class="primary--text text--accent-4"
                    >
                      <template v-slot:default="{ active }">
                        <v-list-item-action>
                          <v-checkbox
                            :input-value="active"
                            color="primary accent-4"
                          ></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title
                            v-text="item.name"
                          ></v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-list-item>
                  </template>
                </v-list-item-group>
              </v-list>
            </v-card-text>
          </v-card>
          <options
            ref="optionsComp"
            v-model="form.options"
            class="mt-4"
            v-if="isOption"
            :before-remove="isKeyCategory ? beforeRemoveOption : null"
            :track-changes="isKeyCategory"
          ></options>
        </v-form>
        <default-field
          v-model="form.default_field"
          type="service"
          v-if="!form.is_calculated"
          :field="form"
        ></default-field>
      </v-card-text>
      <v-card-actions class="pr-5">
        <span v-if="isKeyCategory && keySyncLocked" class="orange--text text-body-1 mr-2">
          A key category sync is in progress. Please wait…
        </span>
        <v-spacer></v-spacer>
        <v-btn text large class="action-btn" color="red" @click="dialog = false">Cancel</v-btn>
        <v-btn
          text
          large
          class="action-btn"
          color="green"
          @click="submit"
          v-if="isAdmin || isDataEntry || isDataEntryAndDispatcher"
          :disabled="isKeyCategory && keySyncLocked"
        >Submit</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Key category delete confirmation -->
  <v-dialog v-model="deleteConfirm.show" max-width="520" persistent>
    <v-card>
      <v-card-title class="red--text">Delete Key Category</v-card-title>
      <v-card-text>
        <p>
          You are about to delete
          <strong>"{{ deleteConfirm.item && (deleteConfirm.item.originalName || deleteConfirm.item.name) }}"</strong>.
        </p>
        <template v-if="deleteConfirm.affectedModels.length">
          <p class="mb-1">
            The following car make models use this key category and will be reset to
            <strong>Default</strong>:
          </p>
          <v-list dense class="pa-0">
            <v-list-item
              v-for="(entry, i) in deleteConfirm.affectedModels"
              :key="i"
              class="pa-0"
            >
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ entry.make }}</v-list-item-title>
                <v-list-item-subtitle>{{ entry.models.join(", ") }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </template>
        <p v-else class="mb-0 grey--text">No car make models are currently using this key category.</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancelDelete">Cancel</v-btn>
        <v-btn text color="red" @click="confirmDelete">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Key category conflict warning -->
  <v-dialog v-model="conflictDialog.show" max-width="560" persistent>
    <v-card>
      <v-card-title class="orange--text text--darken-2">Changes Made By Another User</v-card-title>
      <v-card-text>
        <p class="mb-2">
          While you were editing, someone else already saved changes to some of the same
          key categories:
        </p>
        <v-list dense class="pa-0">
          <v-list-item v-for="(msg, i) in conflictDialog.conflicts" :key="i" class="pa-0">
            <v-list-item-content>
              <v-list-item-subtitle class="text-wrap">{{ msg }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <p class="mt-3 mb-0">
          You can reload the latest list and redo your changes, or submit anyway
          (your version will overwrite what changed).
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="reloadAfterConflict">Reload Latest</v-btn>
        <v-btn text color="red" @click="submitAfterConflict">Submit Anyway</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import { getDate } from "../../../assets/js/common";
import { fieldTypes } from "../../../assets/js/default";
import { serviceFields, functions, appSetting, keyCategoryLogs } from "../../../assets/js/firebase";
import { FIELD } from "../../../assets/js/rules";
import fieldsId from "../../../assets/constants/additonal.fields.constant.js";
import customerModal from "../../../assets/js/customer.modal.js";
import appSettingModel from "../../../assets/js/appSetting.model";
import AddButton from "../../buttons/AddButton.vue";
import EditIconButton from "../../buttons/EditIconButton.vue";
import DefaultField from "../global/DefaultField.vue";
import Options from "../Options.vue";
export default {
  props: {
    serviceId: String,
    item: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  components: { AddButton, Options, EditIconButton, DefaultField },
  data() {
    return {
      dialog: false,
      isValid: false,
      rules: FIELD,
      form: {},
      deleteConfirm: { show: false, resolve: null, item: null, affectedModels: [] },
      freshOptions: null,
      keySyncLocked: false,
      unwatchKeySyncLock: null,
      latestMasterOptions: null,
      unwatchMasterDoc: null,
      conflictDialog: { show: false, conflicts: [], pendingDoc: null },
    };
  },
  computed: {
    isService() {
      return this.$route.name == "template-update";
    },
    isOption() {
      const { field_type } = this.form;
      return field_type == "select" || field_type == "autocomplete";
    },
    types() {
      return fieldTypes.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
    title() {
      return this.id ? "Edit Service Field" : "Add New Service Field";
    },
    id() {
      return this.item.id;
    },
    isKeyCategory() {
      return this.id === fieldsId.KEY_CATEGORY || this.id === customerModal.KEY_CATEGORY;
    },
    templates() {
      return this.$store.getters.templates.filter((item) => {
        return item.type == 0;
      });
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.init();
        if (this.isKeyCategory) {
          this.watchKeySyncLock();
          this.watchMasterOptions();
        }
      } else {
        this.stopWatchingKeySyncLock();
        this.stopWatchingMasterOptions();
      }
    },
  },
  beforeDestroy() {
    this.stopWatchingKeySyncLock();
    this.stopWatchingMasterOptions();
  },
  methods: {
    async submit() {
      const vm = this;
      vm.$refs.form.validate();
      if (!vm.isValid) return;

      if (Array.isArray(vm.form.options)) {
    vm.form.options = vm.form.options.filter(opt => {
      const name = (opt.name || "").trim();
      const value = (opt.value || "").trim();

      // Keep the row if either field has data
      return name !== "" || value !== "";
    }).map(option => ({...option}));
  }
      if (Array.isArray(vm.form.options) && vm.form.options.length) {
        const seen = new Set();
        const duplicate = vm.form.options.find(opt => {
          const key = `${(opt.name || '').trim()}__${(opt.value || '').trim()}`;
          if (seen.has(key)) return true;
          seen.add(key);
          return false;
        });
        if (duplicate) {
          vm.$root.alert(`Duplicate option: name "${duplicate.name}" and value "${duplicate.value}" already exist.`);
          return;
        }
      }
      let name = vm.form.name.trim();
      let lower = name.toLowerCase();
      let updated_at = getDate();
      let doc = { lower, updated_at, name };
      Object.keys(vm.form).forEach((key) => {
        doc[key] = vm.form[key];
      });
      // Strip internal tracking fields — these must not be persisted to Firestore
      if (Array.isArray(doc.options)) {
        // eslint-disable-next-line no-unused-vars
        doc.options = doc.options.map(({ originalValue, originalName, isNew, ...rest }) => rest);
      }
      doc.type = "service";
      doc.name = name;
      if (vm.id) {
        if (vm.isKeyCategory) {
          vm.$root.loader = true;
          const conflicts = await vm.detectConflicts();
          vm.$root.loader = false;
          if (conflicts.length) {
            vm.conflictDialog = { show: true, conflicts, pendingDoc: doc };
            return;
          }
        }
        vm.update(doc);
      } else {
        vm.create(doc);
      }
    },
    async create(doc) {
      const vm = this;
      vm.$root.loader = true;
      try {
        let snap = await serviceFields
          .where("lower", "==", doc.lower)
          .where("removed", "==", false)
          .limit(1)
          .get();
        if (vm.isService) {
          doc.services = [vm.serviceId];
        }
        if (!snap.empty) {
          vm.$root.loader = false;
          vm.$root.alert(
            "Another service field already exists with the same name. Please enter a unique name."
          );
          return;
        }
        doc.created_at = getDate();
        await serviceFields.add(doc);
        vm.$root.loader = false;
      } catch (error) {
        vm.$root.loader = false;
        vm.$root.alert(error.message);
      }
    },
    // Lock TTL: if a lock is older than this, treat it as abandoned (e.g. the
    // Cloud Function instance holding it crashed) instead of blocking every
    // future edit forever. Must match the TTL used server-side in
    // sync-key-category.js, since this is only for the live UI indicator now
    // — the lock itself is acquired/released there, not on the client.
    KEY_SYNC_LOCK_TTL_MS: 5 * 60 * 1000,
    // Live-updates keySyncLocked so the Submit button reflects an in-progress
    // sync started by another user/tab, instead of only finding out after
    // clicking Submit and getting the alert.
    watchKeySyncLock() {
      const vm = this;
      vm.stopWatchingKeySyncLock();
      vm.unwatchKeySyncLock = appSetting
        .doc(appSettingModel.keySyncLockId)
        .onSnapshot((doc) => {
          const data = doc.exists ? doc.data() : null;
          const isStale = data && data.lockedAt &&
            Date.now() - data.lockedAt.toMillis() > vm.KEY_SYNC_LOCK_TTL_MS;
          vm.keySyncLocked = !!(data && data.locked && !isStale);
        });
    },
    stopWatchingKeySyncLock() {
      if (this.unwatchKeySyncLock) {
        this.unwatchKeySyncLock();
        this.unwatchKeySyncLock = null;
      }
      this.keySyncLocked = false;
    },
    // Keeps latestMasterOptions current for every open dialog (not just the
    // one that triggers a save), so a concurrent edit by another user can be
    // detected against this user's in-progress local changes at submit time.
    watchMasterOptions() {
      const vm = this;
      vm.stopWatchingMasterOptions();
      if (!vm.id) return;
      let isFirstSnapshot = true;
      vm.unwatchMasterDoc = serviceFields.doc(vm.id).onSnapshot((doc) => {
        vm.latestMasterOptions = doc.exists ? (doc.data().options || []) : [];
        if (isFirstSnapshot) {
          // Initial attach — form.options was just loaded from the same
          // source, nothing to reconcile yet.
          isFirstSnapshot = false;
          return;
        }
        vm.mergeUntouchedRows();
      });
    },
    // Reconciles rows the user hasn't touched against the latest master data
    // whenever another user's sync completes, so an untouched row never goes
    // stale while this dialog stays open. Red/yellow rows are left alone —
    // the submit-time conflict check handles those.
    async mergeUntouchedRows(freshOptionsOverride) {
      const vm = this;
      const optionsComp = vm.$refs.optionsComp;
      const freshOptions = freshOptionsOverride || vm.latestMasterOptions;
      if (!optionsComp || !Array.isArray(freshOptions)) return;
      const localRows = optionsComp.getRows();
      const renameMap = {};
      const removals = new Set();

      // Resolved for every row missing from freshOptions, touched or not —
      // a touched row still needs its resolved target known so the "added
      // by someone else" step below can recognize it as the same row
      // instead of appending it again as a phantom duplicate. Keyed by id
      // when the row has one (can't be confused with an unrelated row that
      // happens to share the old value), falling back to originalValue for
      // legacy rows saved before ids existed.
      for (const row of localRows) {
        if (!row.originalValue || row.isNew) continue;
        const key = row.id || row.originalValue;
        const stillPresent = row.id
          ? freshOptions.some((f) => f.id === row.id)
          : freshOptions.some((f) => f.value === row.originalValue);
        if (stillPresent) continue;

        const log = await vm.findLatestLogFor(row.id, row.originalValue);
        if (log && log.action === "rename") {
          renameMap[key] = { name: log.newName, value: log.newValue };
        } else {
          removals.add(key);
        }
      }

      optionsComp.mergeMaster(freshOptions, renameMap, removals);
    },
    stopWatchingMasterOptions() {
      if (this.unwatchMasterDoc) {
        this.unwatchMasterDoc();
        this.unwatchMasterDoc = null;
      }
      this.latestMasterOptions = null;
    },
    // A value missing from latestMasterOptions is ambiguous — someone else
    // may have deleted it, or renamed it by changing the value itself (not
    // just the display name), which also makes it vanish under its old
    // value. The options list alone can't tell those apart; the audit log
    // can, since add/delete/rename are logged as distinct actions.
    async findLatestLogFor(id, value) {
      try {
        // Single-field equality only — no orderBy — so this needs no
        // composite index and works immediately. Prefer optionId (stable
        // across renames, so it can't be confused with a different row that
        // happens to reuse an old value) and fall back to previousValue only
        // for legacy rows saved before ids existed.
        const snap = id
          ? await keyCategoryLogs.where("optionId", "==", id).get()
          : await keyCategoryLogs.where("previousValue", "==", value).get();
        if (snap.empty) return null;
        const docs = snap.docs.map((d) => d.data());
        docs.sort((a, b) => {
          const at = a.performedAt ? a.performedAt.toMillis() : 0;
          const bt = b.performedAt ? b.performedAt.toMillis() : 0;
          return bt - at;
        });
        return docs[0];
      } catch (e) {
        console.error("[detectConflicts] log lookup failed:", e);
        return null;
      }
    },
    // Compares this user's in-progress edits against the latest known master
    // state to catch a concurrent delete/rename by someone else that would
    // otherwise be silently overwritten by this submit. Only rows the user
    // actually touched this session (renamed = yellow, marked for delete =
    // red) are checked — a row nobody here is changing isn't a conflict for
    // this submit, it's just something "Reload Latest" will pick up quietly.
    async detectConflicts() {
      const vm = this;
      if (!Array.isArray(vm.latestMasterOptions) || !vm.$refs.optionsComp) return [];
      // Options.vue's onInput() deliberately excludes pendingDelete rows from
      // what it emits to v-model (that's what makes the diff on submit treat
      // "missing from newOptions" as the delete signal) — so a row marked
      // red never appears in vm.form.options at all. Reading the component's
      // full internal list instead is the only way to also check those rows.
      const rows = vm.$refs.optionsComp.getRows();
      const conflicts = [];
      for (const opt of rows) {
        const baseValue = opt.originalValue;
        const baseName = opt.originalName;

        // originalValue is set once, at row creation, and never changes —
        // "" means this row was added this session and has no prior state
        // in master to conflict with.
        if (!baseValue) continue;

        const userRenamed = opt.name !== baseName || opt.value !== baseValue;
        if (!userRenamed && !opt.pendingDelete) continue; // untouched — ignore

        // id first — it can't be confused with a different row that ends up
        // sharing the same old value. Falls back to value for legacy rows
        // saved before ids existed.
        const stillInMaster = opt.id
          ? vm.latestMasterOptions.find((m) => m.id === opt.id)
          : vm.latestMasterOptions.find((m) => m.value === baseValue);

        if (stillInMaster) {
          if (stillInMaster.name !== baseName) {
            conflicts.push(
              opt.pendingDelete
                ? `"${baseName}" was renamed to "${stillInMaster.name}" by another user. Do you still want to delete it?`
                : `"${baseName}" was renamed to "${stillInMaster.name}" by another user while you were also editing it.`
            );
          }
          continue;
        }

        // Not found — check the audit log to tell delete from a
        // value-changing rename before assuming it was deleted.
        const log = await vm.findLatestLogFor(opt.id, baseValue);
        if (log && log.action === "rename") {
          conflicts.push(
            opt.pendingDelete
              ? `"${baseName}" was renamed to "${log.newName}" by another user. Do you still want to delete it?`
              : `"${baseName}" was renamed to "${log.newName}" by another user while you were also editing it.`
          );
          continue;
        }

        if (log && log.action === "delete") {
          conflicts.push(
            opt.pendingDelete
              ? `"${baseName}" was already deleted by another user.`
              : `"${baseName}" was deleted by another user while you were renaming it to "${opt.name}".`
          );
          continue;
        }

        // No log found (e.g. legacy data) — fall back rather than staying silent.
        conflicts.push(
          opt.pendingDelete
            ? `"${baseName}" no longer matches the current key category list — another user may have already removed it.`
            : `"${baseName}" no longer matches the current key category list — another user may have changed it while you were renaming it to "${opt.name}".`
        );
      }
      return conflicts;
    },
    reloadAfterConflict() {
      const vm = this;
      vm.conflictDialog = { show: false, conflicts: [], pendingDoc: null };
      if (Array.isArray(vm.latestMasterOptions)) {
        vm.freshOptions = vm.latestMasterOptions;
        vm.form.options = vm.latestMasterOptions.map((opt) => ({
          id: opt.id,
          name: opt.name,
          value: opt.value,
          originalValue: opt.value,
          originalName: opt.name,
        }));
        vm.$nextTick(() => {
          if (vm.$refs.optionsComp) vm.$refs.optionsComp.refresh();
        });
      }
    },
    submitAfterConflict() {
      const vm = this;
      const doc = vm.conflictDialog.pendingDoc;
      vm.conflictDialog = { show: false, conflicts: [], pendingDoc: null };
      // Without this, update()'s diff baseline (oldOptions) could still be
      // the pre-conflict snapshot — e.g. a pending delete would then be
      // computed against a value another user already renamed away, so the
      // backend would find no model still using it and silently do nothing.
      // Refreshing to the latest known master state first makes "Submit
      // Anyway" actually act on the current key, not a stale one.
      if (Array.isArray(vm.latestMasterOptions)) {
        vm.freshOptions = vm.latestMasterOptions;
      }
      if (doc) vm.update(doc);
    },
    async update(doc) {
      const vm = this;
      vm.$root.loader = true;
      // Only Key Category sync mutates models (reset-to-Default / rename
      // propagation), so only it needs the lock. Type of Key's sync only
      // writes audit logs — no model race to guard against.
      const isKeyField = vm.isKeyCategory;
      try {
        let snap = await serviceFields
          .where("lower", "==", doc.lower)
          .where("where", "==", false)
          .limit(1)
          .get();
        if (!snap.empty && snap.docs[0].id != vm.id) {
          vm.$root.loader = false;
          vm.$root.alert(
            "Another service field already exists with the same name. Please enter a unique name."
          );
          return;
        }

        // Snapshot both before the await — onSnapshot can fire after the
        // Firestore write resolves and reactively overwrite vm.form.options,
        // resetting originalValue before syncKeyCategory reads it.
        // vm.freshOptions (set after a prior save in this session, from a
        // direct re-fetch) is preferred over vm.item.options, which is a
        // prop that only updates once the listener catches up and can be
        // stale relative to what was actually just saved.
        const oldOptions = [...(vm.freshOptions || vm.item.options || [])];
        const newOptions = (vm.form.options || []).map(opt => ({ ...opt }));

        console.log("[syncKeyCategory] vm.id:", vm.id, "| KEY_CATEGORY id:", fieldsId.KEY_CATEGORY, "| match:", vm.id === fieldsId.KEY_CATEGORY);
        if (vm.id === fieldsId.KEY_CATEGORY || vm.id === customerModal.KEY_CATEGORY) {
          // The master write itself happens inside this call now (server
          // side, behind the lock) — not here on the client beforehand.
          await vm.syncKeyCategory(oldOptions, newOptions, vm.id, doc);
        } else {
          await serviceFields.doc(vm.id).update(doc);
        }

        if (
          vm.id === customerModal.TYPE_OF_KEY ||
          (vm.item && vm.item.name && vm.item.name.toUpperCase() === "TYPE OF KEY")
        ) {
          vm.syncKeyType(oldOptions, newOptions);
        }

        // Re-fetch the just-saved doc directly (rather than trusting the
        // local optimistic state or waiting on the prop's onSnapshot to
        // catch up) so the next edit's diff starts from a clean, verified
        // baseline. This is also what prevents a delete/rename that was
        // already submitted from being computed and sent again.
        if (isKeyField && Array.isArray(vm.form.options)) {
          const freshSnap = await serviceFields.doc(vm.id).get();
          const freshOptions = (freshSnap.data() || {}).options || [];
          vm.freshOptions = freshOptions;
          vm.form.options = freshOptions.map(opt => ({
            id: opt.id,
            name: opt.name,
            value: opt.value,
            originalValue: opt.value,
            originalName: opt.name,
          }));
          vm.$nextTick(() => {
            if (vm.$refs.optionsComp) vm.$refs.optionsComp.refresh();
          });
        } else if (vm.form.options) {
          vm.form.options = vm.form.options.map(opt => ({
            ...opt,
            originalValue: opt.value,
            originalName:  opt.name,
          }));
          vm.$nextTick(() => {
            if (vm.$refs.optionsComp) vm.$refs.optionsComp.refresh();
          });
        }

        vm.$root.loader = false;
      } catch (error) {
        // A lock-rejection (error.code === "failed-precondition") means
        // nothing was written — safe to skip the re-fetch. Any other error
        // could have happened *after* the master write already succeeded
        // inside sync-key-category.js (model-sync/logging failed post-write),
        // so the local baseline must still be refreshed from master — the
        // next edit's diff must not be computed against stale pre-write data.
        // Uses the same merge as the live cross-user sync (mergeUntouchedRows/
        // mergeMaster) instead of a blunt overwrite, so rows the user hasn't
        // touched (not renamed, not marked for delete) get synced to master,
        // while whatever they were mid-edit on is left exactly as they left
        // it — nothing is silently wiped out from under them.
        if (isKeyField && error.code !== "failed-precondition") {
          try {
            const freshSnap = await serviceFields.doc(vm.id).get();
            const freshOptions = (freshSnap.data() || {}).options || [];
            vm.freshOptions = freshOptions;
            await vm.mergeUntouchedRows(freshOptions);
          } catch (refetchError) {
            console.error("[update] post-error re-fetch failed:", refetchError);
          }
        }
        vm.$root.loader = false;
        // Firebase callable errors from the server-side lock rejection land
        // here as error.message (set from the HttpsError in
        // sync-key-category.js) — same user-facing wording as before.
        vm.$root.alert(error.message);
      }
    },

    beforeRemoveOption(item) {
      return new Promise((resolve) => {
        if (!item.originalValue && !item.originalName && !item.value && !item.name) {
          resolve(true);
          return;
        }
        const makes = this.$store.getters.makes;
        const matchValue = item.originalValue || item.value;
        const matchName  = item.originalName  || item.name;
        const affected = [];
        makes.forEach((make) => {
          const matchingModels = (make.models || []).filter(
            (m) => m.key_category && (m.key_category === matchValue || m.key_category === matchName)
          );
          if (matchingModels.length) {
            affected.push({ make: make.name, models: matchingModels.map((m) => m.name) });
          }
        });
        this.deleteConfirm = { show: true, resolve, item, affectedModels: affected };
      });
    },
    confirmDelete() {
      this.deleteConfirm.resolve(true);
      this.deleteConfirm = { show: false, resolve: null, item: null, affectedModels: [] };
    },
    cancelDelete() {
      this.deleteConfirm.resolve(false);
      this.deleteConfirm = { show: false, resolve: null, item: null, affectedModels: [] };
    },
    // Matches an old/new option by stable id when both sides have one — an
    // id never changes across a rename, so two people independently renaming
    // the same original row can't get tangled the way value/name string
    // matching does. Falls back to value/name only for legacy rows saved
    // before ids existed.
    matchOptionAcross(list, opt) {
      return list.find(o =>
        (o.id && opt.id) ? o.id === opt.id : (o.value || o.name) === (opt.originalValue || opt.value || opt.name)
      );
    },
    diffOptions(oldOptions, newOptions) {
      const vm = this;
      const addedOptions = newOptions
        .filter(n => !vm.matchOptionAcross(oldOptions, n))
        .map(n => ({ id: n.id, name: n.name, value: n.value }));

      const deletedOptions = oldOptions
        .filter(o => !newOptions.some(n => (o.id && n.id) ? n.id === o.id : (n.originalValue || n.value) === o.value))
        .map(o => ({ id: o.id, name: o.name, value: o.value }));

      const renames = newOptions
        .filter(n => {
          const old = vm.matchOptionAcross(oldOptions, n);
          if (!old) return false;
          return old.value !== n.value || old.name !== n.name;
        })
        .map(n => {
          const old = vm.matchOptionAcross(oldOptions, n);
          return {
            id:           n.id,
            from:         old.value,
            to:           n.value,
            name:         n.name,
            originalName: old.name,
          };
        });

      return { addedOptions, deletedOptions, renames };
    },
    // serviceFieldId/serviceFieldDoc: the master write for Key Category now
    // happens inside this Cloud Function (see sync-key-category.js), guarded
    // by a server-side lock — not on the client before calling it. A Cloud
    // Function keeps running to completion even if the tab that triggered it
    // is closed, so the write and the model-sync/lock either both happen or
    // neither does; a client-held lock had no such guarantee. So this must
    // always be called for a Key Category save (even with no option changes,
    // e.g. a name-only edit), not just when addedOptions/deletedOptions/
    // renames are non-empty.
    syncKeyCategory(oldOptions, newOptions, serviceFieldId, serviceFieldDoc) {
      const { addedOptions, deletedOptions, renames } = this.diffOptions(oldOptions, newOptions);

      console.log("[syncKeyCategory] addedOptions:", addedOptions, "| deletedOptions:", deletedOptions, "| renames:", renames);
      console.log("[syncKeyCategory] calling Cloud Function...");
      // Intentionally not swallowing errors here (e.g. the lock-rejection
      // error) — update() needs to see the rejection so it can alert and
      // skip the post-save re-fetch instead of reporting a false success.
      return functions.httpsCallable("syncKeyCategory")({
        addedOptions, deletedOptions, renames, serviceFieldId, serviceFieldDoc,
      });
    },
    syncKeyType(oldOptions, newOptions) {
      const { addedOptions, deletedOptions, renames } = this.diffOptions(oldOptions, newOptions);

      if (addedOptions.length === 0 && deletedOptions.length === 0 && renames.length === 0) return Promise.resolve();

      console.log("[syncKeyType] calling Cloud Function...");
      return functions.httpsCallable("syncKeyType")({ addedOptions, deletedOptions, renames })
        .catch(e => console.error("[syncKeyType] Cloud Function error:", e));
    },

    init() {
      const vm = this;
      vm.form = {
        name: "",
        services: [],
        field_type: "text",
        is_calculated: false,
        is_required: true,
        is_used: false,
        is_disabled: false,
        is_fixed: false,
        is_searchable: false,
        options: [],
        default_field: {
          type: "global",
          global: "",
          service: {},
        },
      };
      if (vm.id) {
        Object.keys(vm.form).forEach((key) => {
          if (vm.item[key] != undefined) {
            vm.form[key] = vm.item[key];
          }
        });
      }
    },
  },
};
</script>

<style>
.service-list {
  overflow-y: scroll;
}
.service-field-form .v-text-field input,
.service-field-form .v-select__selection,
.service-field-form .v-label,
.service-field-form .v-list-item__title,
.service-field-form .action-btn {
  font-size: 16px !important;
}
</style>
